import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Utensils,
  Timer,
  Mic,
  Camera,
  Clock,
  WifiOff,
  Crown,
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#b87333';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#b87333';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Utensils className="w-5 h-5" />,
      question: "How does Ensemble work?",
      answer: (
        <div className="space-y-2">
          <p>
            Ensemble schedules your entire meal <strong>backwards</strong> from the moment you want to eat.
            Pick a finish time, add your recipes, and Ensemble builds a single step-by-step timeline so
            every dish lands on the table hot.
          </p>
          <ol className="list-decimal pl-5 space-y-1 mt-3">
            <li>Tap <strong>New Meal</strong></li>
            <li>Add recipes (search, URL paste, manual, or photo scan)</li>
            <li>Set the finish time</li>
            <li>Tap <strong>Orchestrate</strong> to generate your timeline</li>
            <li>Tap <strong>Start Cooking</strong> to enter cooking mode</li>
          </ol>
        </div>
      )
    },
    {
      icon: <Timer className="w-5 h-5" />,
      question: "What is cooking mode and how does it work?",
      answer: (
        <div className="space-y-2">
          <p>Cooking mode shows you <strong>one step at a time</strong> with a live timer:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>The current task, its duration, and the cooking zone are front-and-center</li>
            <li>Passive steps (simmer, bake, rest) auto-advance when the timer ends</li>
            <li>Active steps wait for you to tap <strong>Done</strong></li>
            <li>Tap <strong>Late</strong> if you need more time — Ensemble shifts affected tasks forward</li>
            <li>Tap <strong>Skip</strong> to move on</li>
            <li>A progress bar at the top shows overall meal progress</li>
            <li>An active-dishes strip at the bottom shows each recipe's progress</li>
          </ul>
        </div>
      )
    },
    {
      icon: <Camera className="w-5 h-5" />,
      question: "How does Photo Scan work? (Pro)",
      answer: (
        <div className="space-y-2">
          <p>
            Photo Scan uses on-device <strong>Vision OCR</strong> to read recipes from a cookbook page, a
            handwritten note, or a screenshot — no internet required.
          </p>
          <ol className="list-decimal pl-5 space-y-1 mt-3">
            <li>In New Meal, tap <strong>Add Recipe → Photo</strong></li>
            <li>Take a photo or pick one from your library</li>
            <li>Ensemble extracts text, identifies steps, and estimates durations</li>
            <li>Review and edit the parsed recipe, then add it to your meal</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Clear, high-contrast photos work best. Photo Scan is part of Ensemble Pro.
          </p>
        </div>
      )
    },
    {
      icon: <Mic className="w-5 h-5" />,
      question: "How do voice commands work? (Pro)",
      answer: (
        <div className="space-y-2">
          <p>
            Voice commands let you keep cooking while your hands are messy. Tap the mic in the top bar
            of cooking mode, grant microphone + speech permission the first time, then say one of:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>"Done"</strong> / <strong>"Finished"</strong> / <strong>"Complete"</strong> — mark current step complete</li>
            <li><strong>"Skip"</strong> / <strong>"Next"</strong> — skip to the next step</li>
            <li><strong>"Pause"</strong> / <strong>"Stop"</strong> — pause the session</li>
            <li><strong>"Resume"</strong> / <strong>"Continue"</strong> / <strong>"Go"</strong> — resume</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Speech recognition runs through Apple's Speech framework and is not recorded. Voice commands are part of Ensemble Pro.
          </p>
        </div>
      )
    },
    {
      icon: <Clock className="w-5 h-5" />,
      question: "What does \"Running Late\" do?",
      answer: (
        <div className="space-y-2">
          <p>
            If the current step is taking longer than planned, tap <strong>Late</strong> to add 5 minutes
            of breathing room. Ensemble only shifts the tasks that are actually affected:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>All remaining steps in the <em>same recipe</em> move forward</li>
            <li>Other recipes sharing the same exclusive zone (e.g. oven) also shift</li>
            <li>Recipes on different zones are left alone</li>
            <li>Scheduled local notifications for upcoming passive tasks are rescheduled automatically</li>
          </ul>
        </div>
      )
    },
    {
      icon: <WifiOff className="w-5 h-5" />,
      question: "Do I need an internet connection?",
      answer: (
        <div className="space-y-2">
          <p>Ensemble works offline for almost everything:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Offline:</strong> orchestration, cooking mode, timers, notifications, history, photo scan, voice commands, manual recipe entry</li>
            <li><strong>Requires internet:</strong> searching TheMealDB for recipes, and importing a recipe from a URL</li>
          </ul>
        </div>
      )
    },
    {
      icon: <Crown className="w-5 h-5" />,
      question: "What's Ensemble Pro?",
      answer: (
        <div className="space-y-2">
          <p>
            Ensemble Pro is a <strong>one-time purchase</strong> (no subscriptions) that unlocks:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Photo Scan</strong> — import recipes from any photo</li>
            <li><strong>Voice Commands</strong> — hands-free cooking</li>
            <li><strong>Unlimited dishes per meal</strong> — free tier is capped at 3</li>
          </ul>
          <p className="mt-3">
            Tap <strong>Settings → Upgrade to Pro</strong> to purchase, or use the Restore Purchases button if
            you've already bought on another device.
          </p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Refunds are handled by Apple through the App Store.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Ensemble" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Ensemble%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Ensemble
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Ensemble</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 17+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Food & Drink</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/ensemble/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/ensemble/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
